const WORD_TO_DIGIT = {
  zero: "0",
  oh: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
};

/** Convert spoken digit words into a continuous digit string. */
function normalizeSpokenDigits(text) {
  if (!text) return "";
  const tokens = text.toLowerCase().replace(/[^\w\s]/g, " ").split(/\s+/).filter(Boolean);
  return tokens.map((t) => WORD_TO_DIGIT[t] ?? t).join("");
}

function extractMobile(text) {
  const normalized = normalizeSpokenDigits(text);
  const direct = normalized.match(/(?:\+91|91)?([6-9]\d{9})/);
  if (direct) return direct[1];

  const spaced = text.replace(/\D/g, "");
  const match = spaced.match(/([6-9]\d{9})/);
  return match ? match[1] : "";
}

function extractEmail(text) {
  const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0].toLowerCase() : "";
}

function extractPincode(text) {
  const normalized = normalizeSpokenDigits(text);
  const match = normalized.match(/\b(\d{6})\b/);
  return match ? match[1] : "";
}

function extractLabeledValue(text, labels) {
  const pattern = new RegExp(
    `(?:${labels.join("|")})\\s*(?:is|:|-)?\\s*([^,.\\n;]+)`,
    "i"
  );
  const match = text.match(pattern);
  return match ? match[1].trim() : "";
}

function extractCustomerType(text) {
  const lower = text.toLowerCase();
  if (/\bsalaried\b/.test(lower)) return "Salaried";
  if (/\bbusiness\b/.test(lower)) return "Business";
  return "";
}

function cleanValue(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^[\s,.-]+|[\s,.-]+$/g, "")
    .trim();
}

/**
 * Parse free-form voice transcript into enquiry form fields.
 * Designed for Indian CRM phrases; always returns partial matches for user review.
 */
export function parseEnquiryTranscript(rawTranscript) {
  const text = String(rawTranscript || "").trim();
  if (!text) {
    return {
      customerName: "",
      mobile: "",
      email: "",
      businessName: "",
      address: "",
      city: "",
      pincode: "",
      notes: "",
      enquirySource: "",
      customerType: "",
      rawTranscript: "",
    };
  }

  const mobile = extractMobile(text);
  const email = extractEmail(text);
  const pincode = extractPincode(text);
  const customerType = extractCustomerType(text);

  const customerName = cleanValue(
    extractLabeledValue(text, [
      "customer name",
      "customer is",
      "name is",
      "owner name",
      "contact name",
      "person name",
    ])
  );

  const businessName = cleanValue(
    extractLabeledValue(text, [
      "business name",
      "company name",
      "company is",
      "business is",
      "firm name",
      "shop name",
    ])
  );

  const address = cleanValue(
    extractLabeledValue(text, ["address is", "address", "located at", "location is"])
  );

  const city = cleanValue(
    extractLabeledValue(text, ["city is", "city", "from city", "in city"])
  );

  const enquirySource = cleanValue(
    extractLabeledValue(text, [
      "source is",
      "enquiry source",
      "heard from",
      "referral from",
      "came from",
    ])
  );

  let notes = cleanValue(
    extractLabeledValue(text, ["note is", "notes", "remark is", "remarks", "add note"])
  );

  if (!notes) {
    notes = text;
  }

  return {
    customerName,
    mobile,
    email,
    businessName,
    address,
    city,
    pincode,
    notes,
    enquirySource,
    customerType,
    rawTranscript: text,
  };
}

/** Merge parsed voice fields into existing form state without wiping manual edits. */
export function mergeParsedEnquiryFields(current, parsed, { overwriteEmptyOnly = true } = {}) {
  const next = { ...current };
  const fields = [
    "customerName",
    "mobile",
    "email",
    "businessName",
    "address",
    "city",
    "pincode",
    "notes",
    "enquirySource",
    "customerType",
  ];

  fields.forEach((field) => {
    const value = parsed[field];
    if (!value) return;
    if (overwriteEmptyOnly && current[field]) return;
    next[field] = value;
  });

  return next;
}
